import React, { useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

export interface ParticleSimulationProps {
  size: number;
  count: number;
  color: string;
  particleSize: number;
  speed: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export function ParticleSimulation({
  size,
  count,
  color,
  particleSize,
  speed,
}: ParticleSimulationProps) {
  const particlesRef = useRef<Particle[]>([]);

  // Initialize particles
  useEffect(() => {
    const particles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * size,
        y: Math.random() * size,
        vx: (Math.random() - 0.5) * speed * 2,
        vy: (Math.random() - 0.5) * speed * 2,
      });
    }
    particlesRef.current = particles;
  }, [count, size, speed]);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = color;

      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        
        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Bounce off walls
        if (p.x < particleSize || p.x > size - particleSize) p.vx *= -1;
        if (p.y < particleSize || p.y > size - particleSize) p.vy *= -1;

        // Soft collision / repulsion with other particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p2.x - p.x;
          const dy = p2.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const minDist = particleSize * 2.5;

          if (dist < minDist) {
            // Repel
            const force = (minDist - dist) / minDist;
            const ax = (dx / dist) * force * 0.05 * speed;
            const ay = (dy / dist) * force * 0.05 * speed;
            p.vx -= ax;
            p.vy -= ay;
            p2.vx += ax;
            p2.vy += ay;
          }
        }

        // Apply speed limit
        const currentSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (currentSpeed > speed * 1.5) {
          p.vx = (p.vx / currentSpeed) * (speed * 1.5);
          p.vy = (p.vy / currentSpeed) * (speed * 1.5);
        }

        // Draw
        ctx.beginPath();
        ctx.arc(p.x, p.y, particleSize, 0, Math.PI * 2);
        ctx.fill();
      }
    },
    [size, color, particleSize, speed]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
